import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/858ac54c-dfd5-4167-b27a-f45e123850c3/images/d63f37f595e0658621d58c03a1fd1e2440696df309935df300097417ea7e15fb.jpeg";

const lineReveal = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.35 + i * 0.12 },
  }),
};

const Line = ({ children, i, className = "" }) => (
  <div className="overflow-hidden">
    <motion.span
      custom={i}
      variants={lineReveal}
      initial="hidden"
      animate="visible"
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </div>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-screen overflow-hidden grain">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={HERO_IMG} alt="football pitch at night" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#2E6BE6] mix-blend-color opacity-30 pointer-events-none" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/40" />
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 min-h-screen flex flex-col justify-end pb-20 md:pb-28"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mb-6 flex items-center gap-3 text-[#2E6BE6] text-xs uppercase tracking-[0.3em]"
        >
          <span className="w-8 h-px bg-[#2E6BE6]" />
          Football · Fitness · High Performance
        </motion.div>

        <h1 className="font-display font-bold uppercase leading-[0.85] tracking-tighter text-[15vw] md:text-[11vw] lg:text-[9.5vw]">
          <Line i={0}>Elevate</Line>
          <Line i={1}>
            Your <span className="text-[#D4AF37]">Game</span>
          </Line>
        </h1>

        <div className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="max-w-md text-white/85 text-base md:text-lg font-body font-bold"
          >
            One-to-one football and fitness coaching. Sixty focused minutes built around you and your goals — data driven. Pick your slot below.
          </motion.p>
          <motion.button
            data-testid="hero-cta"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            onClick={() => document.getElementById("book")?.scrollIntoView({ behavior: "smooth" })}
            className="group inline-flex items-center gap-3 self-start bg-[#D4AF37] text-black px-7 py-4 text-sm uppercase tracking-[0.15em] font-semibold hover:-translate-y-1 transition-transform"
          >
            Book a Session
            <ArrowDown className="w-4 h-4 transition-transform group-hover:translate-y-1" />
          </motion.button>
        </div>
      </motion.div>
    </section>
  );
}
